import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import BlogCard from './BlogCard';
import { blogAPI, utils } from './api';
import FreeTreatment from '../../components/FreeTreatment';
import { resolveMediaUrl } from '../../utils/media';

const BlogLanding = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    last_page: 1
  });

  const categories = [
    "Cardiac Sciences", "Orthopaedics", "Neurology", "Oncology", 
    "Dental Science", "General Surgery", "Emergency Care"
  ];

  const fetchBlogs = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Starting to fetch blogs...');
      
      const response = await blogAPI.getBlogs(page);
      console.log('Full API response:', response);
      
      // Handle different API response structures
      let blogsData = response.data || response.blogs || response;
      let paginationData = response;
      
      // If response has pagination info at root level
      if (response.data && Array.isArray(response.data)) {
        blogsData = response.data;
        paginationData = response;
      }
      
      // If response is directly an array
      if (Array.isArray(response)) {
        blogsData = response;
        paginationData = {
          current_page: 1,
          total: response.length,
          last_page: 1
        };
      }
      
      if (!blogsData || !Array.isArray(blogsData)) {
        throw new Error('Invalid response format: blogs data not found');
      }

      // Process blogs to add excerpt and read time
      const processedBlogs = blogsData.map(blog => ({
        ...blog,
        excerpt: utils.generateExcerpt(blog.meta_description || blog.title || blog.content || ''),
        read_time: utils.calculateReadTime(blog.content) || '3 min'
      }));

      console.log('Processed blogs:', processedBlogs);
      setBlogs(processedBlogs);
      
      setPagination({
        current_page: paginationData.current_page || 1,
        total: paginationData.total || processedBlogs.length,
        last_page: paginationData.last_page || paginationData.total_pages || 1
      });
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to load blogs. Please try again later.';
      setError(errorMessage);
      console.error('Error in fetchBlogs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handlePageChange = (page) => {
    fetchBlogs(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Enhanced error handling with retry button
  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>
          <ErrorText>{error}</ErrorText>
          <RetryButton onClick={() => fetchBlogs()}>
            Try Again
          </RetryButton>
        </ErrorMessage>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <Spinner />
          Loading blogs...
        </LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <>
        
    <PageContainer>
      <Header>
        <PageTitle>Healthcare Blogs</PageTitle>
        <Subtitle>Expert medical insights and health tips</Subtitle>
      </Header>
      
      <ContentWrapper>
        <MainContent>
          {blogs.length === 0 && !loading ? (
            <NoBlogsMessage>
              No blogs found. Please check back later.
            </NoBlogsMessage>
          ) : (
            <>
              <BlogsGrid>
                {blogs.map(blog => (
                  <BlogCard key={blog.id} blog={blog} />
                ))}
              </BlogsGrid>
              
              {pagination.last_page > 1 && (
                <Pagination>
                  <PageNumbers>
                    {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                      <PageNumber 
                        key={page} 
                        active={page === pagination.current_page}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </PageNumber>
                    ))}
                  </PageNumbers>
                  <ResultsInfo>
                    Showing page {pagination.current_page} of {pagination.last_page} • {pagination.total} total articles
                  </ResultsInfo>
                </Pagination>
              )}
            </>
          )}
        </MainContent>
        
        <Sidebar>
          <SidebarSection>
            <SidebarTitle>Popular Blogs</SidebarTitle>
            {blogs.slice(0, 3).map(blog => (
              <PopularBlog key={blog.id} onClick={() => window.location.href = `/blog/${blog.slug}`}>
                <PopularBlogImage 
                  src={resolveMediaUrl(blog.featured_image_url)} 
                  alt={blog.title}
                  /* onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/80x80/f8fafc/718096?text=Image';
                  }} */
                />
                <PopularBlogContent>
                  <PopularBlogTitle>{blog.title}</PopularBlogTitle>
                  <BlogMeta>
                    {blog.author_name} • {utils.formatDate(blog.published_at)}
                  </BlogMeta>
                </PopularBlogContent>
              </PopularBlog>
            ))}
          </SidebarSection>
          
{/*       <SidebarSection>
            <SidebarTitle>Categories</SidebarTitle>
            <CategoryList>
              {categories.map(category => (
                <CategoryItem key={category}>
                  {category}
                </CategoryItem>
              ))}
            </CategoryList>
          </SidebarSection> */}
        </Sidebar>
      </ContentWrapper>
    </PageContainer>
    <FreeTreatment/>

    
    </>
    
  );
};

// Additional Styled Components
const ErrorText = styled.div`
  margin-bottom: 15px;
`;

const RetryButton = styled.button`
  background: #4299e1;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    background: #3182ce;
  }
`;

const Spinner = styled.div`
  border: 3px solid #f3f3f3;
  border-top: 3px solid #345FA5;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const NoBlogsMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #345FA5;
  font-size: 1.1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
`;

// Styled Components (same as before with additions)
const PageContainer = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: 'Inter', sans-serif;
  background-color: #f8fafc;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
  color: #345FA5;
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin: 20px 0;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: #345FA5;
  margin: 0 0 16px 0;
  font-weight: 800;
  background: linear-gradient(135deg, #345FA5 0%, #345FA5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

    @media (max-width: 600px){
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #345FA5;
  margin: 0;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const MainContent = styled.div``;

const BlogsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  background-color: ${props => props.active ? '#4299e1' : 'white'};
  color: ${props => props.active ? 'white' : '#4a5568'};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.active ? '#3182ce' : '#edf2f7'};
  }
`;

const ResultsInfo = styled.div`
  color: #718096;
  font-weight: 500;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const SidebarSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const SidebarTitle = styled.h3`
  font-size: 1.5rem;
  color: #2d3748;
  margin: 0 0 25px 0;
  padding-bottom: 15px;
  border-bottom: 2px solid #e2e8f0;
  font-weight: 700;
`;

const PopularBlog = styled.div`
  display: flex;
  gap: 15px;
  padding: 20px 0;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f7fafc;
    border-radius: 8px;
    padding: 20px 15px;
    margin: 0 -15px;
  }
`;

const PopularBlogImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
`;

const PopularBlogContent = styled.div`
  flex: 1;
`;

const PopularBlogTitle = styled.h4`
  font-size: 0.9rem;
  color: #2d3748;
  margin: 0 0 8px 0;
  line-height: 1.4;
  font-weight: 600;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlogMeta = styled.div`
  color: #718096;
  font-size: 0.75rem;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
`;

const CategoryItem = styled.div`
  padding: 15px 20px;
  background-color: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  color: #4a5568;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #edf2f7;
    border-color: #cbd5e0;
    transform: translateX(5px);
  }
`;

export default BlogLanding;



